import { Shield, Zap, Users, Award, ChevronRight, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Category } from "../types/category";
import dbJson from "../data/db.json";

export const HomePage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  console.log("TEST_FROM_FRONTEND");
  console.log("TEST_ROUTE_CHANGE");

  useEffect(() => {
    setCategories(dbJson.categoriesData);
    setLoading(false);
  }, []);

  const features = [
    { icon: Shield, title: "Надёжность", description: "Более 20 лет опыта разработки и производства" },
    { icon: Zap, title: "Инновации", description: "Современные технологии и решения" },
    { icon: Users, title: "Поддержка", description: "Полный цикл сервисного обслуживания" },
    { icon: Award, title: "Качество", description: "Сертифицированное производство" },
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-slate-50 to-white overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230056D2' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="text-[#0056D2] font-mono text-sm uppercase tracking-wider mb-4">
                С 2003 года
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                Системы мониторинга и управления
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
                Разработка и производство радиоэлектронного оборудования для дистанционного контроля технологических процессов, автоматизации зданий и предприятий.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products" className="btn-primary" data-testid="hero-catalog-btn">
                  Каталог продукции
                  <ChevronRight size={18} className="ml-2" />
                </Link>
                <Link to="/contacts" className="btn-secondary" data-testid="hero-contact-btn">
                  Связаться с нами
                </Link>
              </div>
            </div>

            <div className="relative animate-fade-in hidden lg:block">
              <div className="relative rounded-sm overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/17489157/pexels-photo-17489157.jpeg?w=600&h=500&fit=crop"
                  alt="Stadis Engineering"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0056D2] text-white p-6 rounded-sm shadow-xl">
                <p className="text-3xl font-bold">20+</p>
                <p className="text-sm opacity-80">лет на рынке</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center mx-auto mb-4">
                  <feature.icon size={24} className="text-[#0056D2]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-slate-50" data-testid="categories-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">Направления продукции</h2>
            <p className="section-subtitle mx-auto">
              Широкий спектр решений для различных отраслей
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-40 bg-slate-200 animate-pulse rounded-sm" 
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.slice(0, 6).map((category, index) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group bg-white border border-slate-200 p-6 hover:border-[#0056D2] hover:shadow-lg transition-all duration-300 rounded-sm animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`category-card-${category.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-[#0056D2] transition-colors mb-2">
                        {category.name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {category.subcategories.length} подкатегорий
                      </p>
                    </div>
                    <ArrowRight size={20} className="text-slate-400 group-hover:text-[#0056D2] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/products" className="btn-secondary" data-testid="view-all-products-btn">
              Смотреть весь каталог
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#0056D2] font-mono text-sm uppercase tracking-wider mb-4">
                О компании
              </p>
              <h2 className="section-title mb-6">Профессионалы своего дела</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  ООО «Промтемика» — производственная компания, специализирующаяся на разработке и производстве радиоэлектронного оборудования и программного обеспечения в области дистанционных систем контроля и управления.
                </p>
                <p>
                  Наш коллектив существует с 2003 года. Основу компании составляют высококвалифицированные дипломированные инженеры и программисты.
                </p>
                <p>
                  Использование инновационных решений в нашей продукции обеспечивает её применимость в транспорте, строительстве, производстве и медицине.
                </p>
              </div>
              <Link to="/services" className="btn-primary mt-8 inline-flex" data-testid="about-services-btn">
                Наши услуги
                <ChevronRight size={18} className="ml-2" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1621077578861-3dd3bd5f3aba?w=600&h=400&fit=crop"
                alt="Technology"
                className="rounded-sm shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-6 rounded-sm shadow-xl hidden md:block">
                <p className="text-3xl font-bold text-[#0056D2]">100+</p>
                <p className="text-sm opacity-80">успешных проектов</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0056D2]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-4">
            Готовы к сотрудничеству?
          </h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Свяжитесь с нами для консультации по выбору оборудования или обсуждения индивидуального проекта
          </p>
          <Link to="/contacts" className="inline-flex items-center justify-center bg-white text-[#0056D2] font-semibold text-sm uppercase tracking-wide py-3 px-8 rounded-sm hover:bg-slate-100 transition-colors" data-testid="cta-contact-btn">
            Связаться с нами
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};